import React, { useEffect, useMemo, useRef, useState, FormEvent } from "react";
import { Loader2, AlertCircle, AlertTriangle, CheckCircle2, Download } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { RootState } from "../../store/store";
import { fetchProcessorList, fetchProgramList, Program } from "../../features/program/ProgramSlice";
import {
  addPreauthVpas,
  clearPreauthVpas,
  clearPreauthVpaSaveError,
  clearPreauthVpaSyncResult,
  fetchPreauthVpas,
  updatePreauthVpaStatus,
  VpaBrand,
} from "../../features/preauthVpa/PreauthVpaSlice";
import { getProcessorBrand } from "../Programs/BankDetailsSections";
import { downloadVpaTemplate, parseVpaSheet, VPA_REGEX } from "./vpaSheet";

const YESBANK_PROGRAM_CODE_LENGTH = 7;

// Processors that support program VPA registration, and how each one treats VPAs.
//  - YESBANK: preauth service. It matches payeeVpa exactly, so VPAs are lowercased; max 50 chars.
//  - ICICI_CBDC: VPAs become the rule's acceptance list at issuance and are pushed to existing rules.
const VPA_BRANDS: Record<VpaBrand, { maxLength: number; lowercase: boolean }> = {
  YESBANK: { maxLength: 50, lowercase: true },
  ICICI_CBDC: { maxLength: 100, lowercase: false },
};

const asVpaBrand = (processorType: string): VpaBrand | null => {
  const brand = getProcessorBrand(processorType);
  return brand === "YESBANK" || brand === "ICICI_CBDC" ? brand : null;
};

// Program.processorType is the numeric processorId (FK), not the processor name,
// so the name (e.g. "YESBANK") is resolved from the joined column when
// present, otherwise from the processor list.
const getProcessorName = (p: Program, processorsById: Map<number, string>): string => {
  const joined = p["processor.processorType"];
  if (typeof joined === "string" && joined) return joined;
  if (typeof p.processorTypeDisplay === "string" && p.processorTypeDisplay) return p.processorTypeDisplay;
  return processorsById.get(Number(p.processorType)) || "";
};

const formatDate = (value: string) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

const SelectArrow = () => (
  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  </div>
);

const selectClass =
  "block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none disabled:opacity-50";

const PreauthVpaManager: React.FC = () => {
  const dispatch = useAppDispatch();
  const { programList, processorList, programLoading, programError } = useAppSelector((state: RootState) => state.root.program);
  const { vpas, listLoading, listError, saving, saveError, syncResult } = useAppSelector((state: RootState) => state.root.preauthVpa);

  const [processorId, setProcessorId] = useState("");
  const [programCode, setProgramCode] = useState("");
  const [vpaInput, setVpaInput] = useState("");
  const [inputError, setInputError] = useState("");
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkErrors, setBulkErrors] = useState<string[]>([]);
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dispatch(fetchProgramList({ limit: 1000 }));
    dispatch(fetchProcessorList());
    return () => {
      dispatch(clearPreauthVpas());
    };
  }, [dispatch]);

  const processorsById = useMemo(
    () => new Map<number, string>((processorList || []).map((pr) => [Number(pr.processorId), pr.processorType])),
    [processorList],
  );

  // Only processors that have a VPA backend
  const supportedProcessors = useMemo(
    () => (processorList || []).filter((pr) => asVpaBrand(pr.processorType) !== null),
    [processorList],
  );
  const selectedProcessor = supportedProcessors.find((pr) => String(pr.processorId) === processorId) || null;
  const brand: VpaBrand | null = selectedProcessor ? asVpaBrand(selectedProcessor.processorType) : null;

  // Programs linked to the selected processor (YesBank: only Pre Auth = Yes, the ones that need VPAs)
  const eligiblePrograms = useMemo(() => {
    if (!selectedProcessor || !brand) return [];
    return (programList || []).filter((p) => {
      const linkedId = Number(p["processor.processorId"] ?? p.processorType);
      const linked = Number.isFinite(linkedId)
        ? linkedId === Number(selectedProcessor.processorId)
        : asVpaBrand(getProcessorName(p, processorsById)) === brand;
      if (!linked) return false;
      return brand === "YESBANK" ? p.bankDetails?.preAuth === true : true;
    });
  }, [programList, selectedProcessor, brand, processorsById]);

  const selectedProgram = eligiblePrograms.find((p) => p.programCode === programCode) || null;
  const codeLengthWarning =
    brand === "YESBANK" && selectedProgram && selectedProgram.programCode.length !== YESBANK_PROGRAM_CODE_LENGTH
      ? `Program code "${selectedProgram.programCode}" is not ${YESBANK_PROGRAM_CODE_LENGTH} characters. Preauth derives the program code from the UMN by position, so VPAs for this program will not match.`
      : "";

  const resetProgramState = () => {
    setProgramCode("");
    setVpaInput("");
    setInputError("");
    setBulkFile(null);
    setBulkErrors([]);
    setBulkMessage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    dispatch(clearPreauthVpas());
  };

  const handleProcessorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProcessorId(e.target.value);
    resetProgramState();
  };

  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    resetProgramState();
    setProgramCode(code);
    if (code && brand) dispatch(fetchPreauthVpas({ brand, programCode: code }));
  };

  const handleAdd = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!brand) return;
    dispatch(clearPreauthVpaSaveError());
    dispatch(clearPreauthVpaSyncResult());
    setBulkMessage("");

    const { maxLength, lowercase } = VPA_BRANDS[brand];
    const typed = vpaInput.trim();
    const vpa = lowercase ? typed.toLowerCase() : typed;
    if (!vpa) {
      setInputError("Please enter a VPA.");
      return;
    }
    if (vpa.length > maxLength || !VPA_REGEX.test(vpa)) {
      setInputError(`Enter a valid VPA (e.g. handle@psp, max ${maxLength} characters).`);
      return;
    }
    if (vpas.some((v) => v.vpa.toLowerCase() === vpa.toLowerCase())) {
      setInputError("This VPA already exists for the selected program.");
      return;
    }
    setInputError("");

    const result = await dispatch(addPreauthVpas({ brand, programCode, vpas: [vpa] }));
    if (addPreauthVpas.fulfilled.match(result)) {
      setVpaInput("");
    }
  };

  const handleToggleStatus = async (id: number, vpa: string, currentlyActive: boolean) => {
    if (!brand) return;
    const action = currentlyActive ? "deactivate" : "activate";
    const note = brand === "ICICI_CBDC" ? "\n\nExisting rules for this program will be updated at the bank." : "";
    if (!window.confirm(`Are you sure you want to ${action} ${vpa}?${note}`)) return;

    dispatch(clearPreauthVpaSyncResult());
    setBulkMessage("");
    setUpdatingId(id);
    await dispatch(updatePreauthVpaStatus({ brand, programCode, id, isActive: !currentlyActive }));
    setUpdatingId(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBulkFile(e.target.files?.[0] || null);
    setBulkErrors([]);
    setBulkMessage("");
    dispatch(clearPreauthVpaSaveError());
  };

  const handleBulkUpload = async () => {
    if (!brand || !bulkFile) {
      setBulkErrors(["Please choose an .xlsx file."]);
      return;
    }
    dispatch(clearPreauthVpaSaveError());
    dispatch(clearPreauthVpaSyncResult());
    setBulkMessage("");
    setBulkBusy(true);
    try {
      const { vpas: parsed, errors } = await parseVpaSheet(bulkFile, VPA_BRANDS[brand]);
      if (errors.length) {
        setBulkErrors(errors);
        return;
      }
      setBulkErrors([]);

      const result = await dispatch(addPreauthVpas({ brand, programCode, vpas: parsed }));
      if (addPreauthVpas.fulfilled.match(result)) {
        setBulkMessage(`Uploaded ${parsed.length} VPA${parsed.length === 1 ? "" : "s"} (VPAs that already exist are left unchanged).`);
        setBulkFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } finally {
      setBulkBusy(false);
    }
  };

  const failedCount = syncResult?.failed ?? 0;

  return (
    <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 mb-8">
      {/* Processor + program selection, add VPA, bulk upload */}
      <div className="bg-white rounded-xl p-6 border-[1px] border-cardborder mb-8">
        <div className="mb-6">
          <label htmlFor="vpaProcessor" className="block text-sm font-medium text-gray-700 mb-2">
            Processor
          </label>
          <div className="relative">
            <select
              id="vpaProcessor"
              value={processorId}
              onChange={handleProcessorChange}
              disabled={!processorList}
              className={selectClass}
            >
              <option value="">Select Processor</option>
              {supportedProcessors.map((pr) => (
                <option key={pr.processorId} value={String(pr.processorId)}>
                  {pr.name || pr.processorType}
                </option>
              ))}
            </select>
            <SelectArrow />
          </div>
          {processorList && supportedProcessors.length === 0 && (
            <p className="text-sm text-gray-500 mt-2">No processors that support VPA registration were found.</p>
          )}
        </div>

        {selectedProcessor && (
          <div className="mb-6">
            <label htmlFor="preauthProgram" className="block text-sm font-medium text-gray-700 mb-2">
              Program
              {brand === "YESBANK" && <span className="ml-2 text-xs text-gray-400 font-normal">(YesBank programs with Pre Auth = Yes)</span>}
            </label>
            <div className="relative">
              <select
                id="preauthProgram"
                value={programCode}
                onChange={handleProgramChange}
                disabled={programLoading && !programList}
                className={selectClass}
              >
                <option value="">Select Program</option>
                {eligiblePrograms.map((p) => (
                  <option key={p.programId} value={p.programCode}>
                    {p.programName} ({p.programCode})
                  </option>
                ))}
              </select>
              <SelectArrow />
            </div>
            {programError && !programList && (
              <div className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {String(programError)}
              </div>
            )}
            {!programLoading && programList && eligiblePrograms.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">
                {brand === "YESBANK" ? "No YesBank programs with Pre Auth enabled were found." : "No programs are linked to this processor."}
              </p>
            )}
            {codeLengthWarning && (
              <div className="mt-2 flex items-start gap-2 text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2" role="alert">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{codeLengthWarning}</span>
              </div>
            )}
          </div>
        )}

        {programCode && brand && (
          <>
            <form onSubmit={handleAdd} noValidate>
              <label htmlFor="preauthVpaInput" className="block text-sm font-medium text-gray-700 mb-2">
                Add VPA
              </label>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <input
                    id="preauthVpaInput"
                    type="text"
                    value={vpaInput}
                    maxLength={VPA_BRANDS[brand].maxLength}
                    placeholder="e.g. fincentive.abc@ypay"
                    onChange={(e) => {
                      setVpaInput(e.target.value);
                      setInputError("");
                      if (saveError) dispatch(clearPreauthVpaSaveError());
                    }}
                    aria-invalid={Boolean(inputError)}
                    className={`block w-full px-3 py-2 border ${inputError ? "border-red-500" : "border-gray-300"} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
                  />
                  {inputError && (
                    <div className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {inputError}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed h-fit"
                >
                  {saving && updatingId === null && !bulkBusy ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Adding...
                    </span>
                  ) : (
                    "Add VPA"
                  )}
                </button>
              </div>
            </form>

            {/* Bulk upload */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="vpaBulkFile" className="block text-sm font-medium text-gray-700">
                  Bulk upload (.xlsx)
                  <span className="ml-2 text-xs text-gray-400 font-normal">One VPA per row in the first column. All are added as active.</span>
                </label>
                <button
                  type="button"
                  onClick={downloadVpaTemplate}
                  className="flex items-center gap-1 text-sm text-brand hover:underline focus:outline-none"
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </button>
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <input
                  id="vpaBulkFile"
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-700 border border-gray-300 rounded-md bg-white file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-gray-100 file:text-sm"
                />
                <button
                  type="button"
                  onClick={handleBulkUpload}
                  disabled={saving || bulkBusy || !bulkFile}
                  className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed h-fit whitespace-nowrap"
                >
                  {bulkBusy || (saving && updatingId === null && bulkFile) ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </span>
                  ) : (
                    "Upload VPAs"
                  )}
                </button>
              </div>
              {bulkErrors.length > 0 && (
                <div className="mt-2 text-sm text-red-500" role="alert">
                  <div className="flex items-center gap-1 font-medium">
                    <AlertCircle className="w-4 h-4" />
                    Nothing was uploaded. Fix these and try again:
                  </div>
                  <ul className="list-disc ml-9 mt-1 max-h-40 overflow-y-auto">
                    {bulkErrors.slice(0, 50).map((err) => (
                      <li key={err}>{err}</li>
                    ))}
                    {bulkErrors.length > 50 && <li>…and {bulkErrors.length - 50} more</li>}
                  </ul>
                </div>
              )}
              {bulkMessage && (
                <div className="mt-2 text-sm text-green-700 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  {bulkMessage}
                </div>
              )}
            </div>
          </>
        )}

        {saveError && (
          <div className="text-red-500 text-sm mt-3 flex items-center gap-1" role="alert">
            <AlertCircle className="w-4 h-4" />
            {saveError}
          </div>
        )}

        {/* Update Rule sync outcome (ICICI_CBDC): counts only, details are in the server logs */}
        {syncResult && (
          <div className="mt-4 text-sm">
            {syncResult.error ? (
              <div className="flex items-start gap-2 text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" role="alert">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  The VPA change was saved, but updating the existing rules failed. Run the sync script for program{" "}
                  <b>{syncResult.programCode}</b>.
                </span>
              </div>
            ) : syncResult.reason ? (
              <div className="text-gray-600">{syncResult.reason}</div>
            ) : (
              <>
                <table className="text-sm border border-gray-200 rounded-md">
                  <thead>
                    <tr className="text-left text-xs font-medium text-gray-500 bg-gray-50">
                      <th className="px-4 py-2">Total rules</th>
                      <th className="px-4 py-2">Updated</th>
                      <th className="px-4 py-2">Already up to date</th>
                      <th className="px-4 py-2">Failed</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-2">{syncResult.total ?? 0}</td>
                      <td className="px-4 py-2 text-green-700">{syncResult.updated ?? 0}</td>
                      <td className="px-4 py-2">{syncResult.alreadyInSync ?? 0}</td>
                      <td className={`px-4 py-2 ${failedCount > 0 ? "text-red-700 font-medium" : ""}`}>{failedCount}</td>
                    </tr>
                  </tbody>
                </table>
                {failedCount > 0 ? (
                  <div className="mt-2 flex items-start gap-2 text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2" role="alert">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      {failedCount} rule(s) could not be updated and still hold the old VPA list. Run the sync script for program{" "}
                      <b>{syncResult.programCode}</b> to retry.
                    </span>
                  </div>
                ) : (
                  <div className="mt-2 flex items-center gap-1 text-green-700">
                    <CheckCircle2 className="w-4 h-4" />
                    {syncResult.total ? "All rules are in sync." : "No existing rules to update."}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* VPA list */}
      {programCode && (
        <div className="bg-white rounded-xl p-6 border-[1px] border-cardborder">
          <div className="font-semibold text-lg mb-4">
            VPAs for {selectedProgram?.programName || programCode}
            <span className="ml-2 text-sm font-normal text-gray-500">({vpas.length})</span>
          </div>

          {listLoading ? (
            <div className="flex justify-center items-center gap-2 py-8">
              <Loader2 className="w-8 h-8 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          ) : listError ? (
            <div className="flex items-center gap-2 justify-center text-red-500 py-8">
              <AlertCircle className="w-5 h-5" />
              <span>{listError}</span>
            </div>
          ) : vpas.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No VPAs added for this program yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4">VPA</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Created</th>
                    <th className="py-2 pr-4">Last Updated</th>
                    <th className="py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {vpas.map((row) => (
                    <tr key={row.id} className="border-b border-gray-100 last:border-0">
                      <td className="py-3 pr-4 text-gray-900">{row.vpa}</td>
                      <td className="py-3 pr-4">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            row.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                          }`}
                        >
                          {row.isActive ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-gray-600">{formatDate(row.createdAt)}</td>
                      <td className="py-3 pr-4 text-gray-600">{formatDate(row.updatedAt)}</td>
                      <td className="py-3 text-right">
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => handleToggleStatus(row.id, row.vpa, row.isActive)}
                          className={`px-3 py-1 text-xs font-medium rounded-md border focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                            row.isActive
                              ? "text-red-700 border-red-300 hover:bg-red-50"
                              : "text-green-700 border-green-300 hover:bg-green-50"
                          }`}
                        >
                          {saving && updatingId === row.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : row.isActive ? (
                            "Deactivate"
                          ) : (
                            "Activate"
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PreauthVpaManager;
